<script setup lang="ts">
const navigation = [
  { name: "Features", href: "#features" },
  { name: "Pricing", href: "#pricing" },
  { name: "Resources", href: "#resources" },
  { name: "Contact", href: "#support" },
];

// Stats for social proof with icons - Focus on value propositions
const stats = [
  { value: "6+", label: "Exchanges Supported", icon: "i-heroicons-arrow-path" },
  { value: "Real-time", label: "Price Updates", icon: "i-heroicons-bolt" },
  { value: "Free", label: "14-Day Trial", icon: "i-heroicons-gift" },
  { value: "Secure", label: "API-Only Access", icon: "i-heroicons-shield-check" },
];

// Mobile menu state — managed here so TopNavBar stays a pure presentation component
const mobileMenuOpen = ref(false);
const toggleMobileMenu = () => { mobileMenuOpen.value = !mobileMenuOpen.value; };

// CTA handlers
const handleStartTrial = () => {
  navigateTo("/login");
};

const handleWatchDemo = () => {
  // Could open video modal or navigate to demo page
};

// Handle image loading errors
const handleImageError = (event: Event, exchangeKey: string) => {
  const img = event.target as HTMLImageElement;
  img.src = `https://via.placeholder.com/40x40/a855f7/ffffff?text=${exchangeKey[0]?.toUpperCase()}`;
};
</script>

<template>
  <div class="relative bg-background">

    <!-- ─── Sticky glass navbar ──────────────────────────────────────────────
         Plain <header> replaces HeadlessUI <Popover> — the Popover component
         wraps ALL children in an internal PortalWrapper (teleport), which
         causes SSR hydration mismatches.  Mobile menu is toggled with a plain
         Vue ref instead.
    ──────────────────────────────────────────────────────────────────────── -->
    <header class="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/60 transition-all duration-300">

      <TopNavBar
        :navigation="navigation"
        :mobile-menu-open="mobileMenuOpen"
        @toggle-mobile-menu="toggleMobileMenu" />

      <!-- Mobile dropdown panel — slides down below the sticky nav bar -->
      <transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="-translate-y-2 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="-translate-y-2 opacity-0">
        <div
          v-if="mobileMenuOpen"
          class="absolute inset-x-0 top-full z-40 md:hidden shadow-lg">
          <LazyPopoverMenuItems
            :navigation="navigation"
            @close="mobileMenuOpen = false" />
        </div>
      </transition>
    </header>

    <!-- ─── Hero content (overflow-hidden only here for gradient clipping) ── -->
    <div class="relative overflow-hidden">

      <!-- Animated gradient background (decorative) -->
      <div class="absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
        <div
          class="absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:left-48 lg:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]">
          <div
            class="aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-primary via-purple-600 to-secondary opacity-10 animate-pulse"
            style="
              clip-path: polygon(
                73.6% 51.7%,
                91.7% 11.8%,
                100% 46.4%,
                97.4% 82.2%,
                92.5% 84.9%,
                75.7% 64%,
                55.3% 47.5%,
                46.5% 49.4%,
                45% 62.9%,
                50.3% 87.2%,
                21.3% 64.1%,
                0.1% 100%,
                5.4% 51.1%,
                21.4% 63.9%,
                58.9% 0.2%,
                73.6% 51.7%
              );
            " />
        </div>
      </div>

      <!-- Hero Section -->
      <div class="relative px-6 pt-20 pb-32 mx-auto max-w-7xl sm:pt-28 lg:px-8">
        <div class="max-w-4xl mx-auto text-center">
          <!-- Badge with enhanced animation -->
          <div class="inline-flex items-center px-4 py-2 mb-8 text-sm font-semibold rounded-full bg-primary/10 border border-primary/20 hover:bg-primary/15 hover:scale-105 transition-all duration-200 cursor-default">
            <span class="relative flex items-center">
              <span class="text-primary font-bold">
                ✨ New
              </span>
              <span class="absolute -top-1 -right-1 flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
            </span>
            <span class="mx-3 text-border">|</span>
            <span class="text-foreground font-medium">Real-time portfolio tracking is here</span>
          </div>

          <!-- Main Headline -->
          <h1
            class="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl">
            Track Your Crypto.
            <span
              class="block mt-3 bg-gradient-to-r from-primary via-purple-600 to-secondary bg-clip-text text-transparent">
              Master Your Trades.
            </span>
          </h1>

          <!-- Description -->
          <p class="max-w-2xl mx-auto mt-8 text-xl leading-relaxed sm:text-2xl text-muted-foreground font-medium">
            The most powerful crypto portfolio tracker for serious traders. Track trades across multiple exchanges, analyze performance, and optimize your strategy—all in one place.
          </p>

          <!-- CTA Buttons -->
          <div class="flex flex-col items-center justify-center gap-4 mt-12 sm:flex-row">
            <button
              @click="handleStartTrial"
              class="group w-full px-8 py-4 text-lg font-semibold transition-all duration-200 rounded-lg shadow-lg sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
              <span class="flex items-center justify-center gap-2">
                Start Free Trial
                <UIcon name="i-heroicons-arrow-right" class="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
            <button
              @click="handleWatchDemo"
              class="group w-full px-8 py-4 text-lg font-semibold transition-all duration-200 rounded-lg shadow-sm sm:w-auto bg-card text-foreground border-2 border-border hover:bg-accent hover:border-primary/30 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
              <span class="flex items-center justify-center gap-2">
                <UIcon name="i-heroicons-play-circle" class="w-6 h-6" />
                Watch Demo
              </span>
            </button>
          </div>

          <!-- Social Proof Stats -->
          <div class="grid grid-cols-2 gap-8 mx-auto mt-16 sm:gap-12 lg:grid-cols-4 max-w-4xl">
            <div
              v-for="stat in stats"
              :key="stat.label"
              class="group flex flex-col items-center p-4 rounded-xl transition-all duration-300 hover:bg-card/50 hover:scale-105">
              <div class="flex items-center gap-2 mb-2">
                <UIcon :name="stat.icon" class="w-5 h-5 text-primary" />
                <div class="text-3xl font-bold text-foreground sm:text-4xl">
                  {{ stat.value }}
                </div>
              </div>
              <div class="text-sm font-medium text-muted-foreground">
                {{ stat.label }}
              </div>
            </div>
          </div>
        </div>

        <!-- Product Screenshot -->
        <div class="relative max-w-6xl mx-auto mt-20">
          <div
            class="relative rounded-2xl bg-card/40 backdrop-blur-sm border border-border shadow-2xl p-2 sm:p-4 ring-1 ring-border/50 transition-all duration-300 hover:ring-2 hover:ring-primary/20 group">
            <LazyNuxtImg
              src="/homepage-screen-close.png"
              alt="VimiCrypto Dashboard"
              height="1442"
              class="w-full rounded-lg shadow-xl transition-transform duration-300 group-hover:scale-[1.01]" />

            <!-- Floating Feature Cards -->
            <div class="absolute hidden lg:block -left-12 top-1/4 animate-float" style="animation-delay: 0s">
              <div
                class="px-4 py-3 rounded-lg bg-card border border-border shadow-xl backdrop-blur-sm hover:scale-110 transition-transform duration-200 cursor-default">
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center w-10 h-10 rounded-full bg-green-500/10">
                    <span class="text-xl">📈</span>
                  </div>
                  <div>
                    <div class="text-sm font-semibold text-foreground">+12.5%</div>
                    <div class="text-xs text-muted-foreground">This Month</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="absolute hidden lg:block -right-12 top-1/3 animate-float" style="animation-delay: 1s">
              <div
                class="px-4 py-3 rounded-lg bg-card border border-border shadow-xl backdrop-blur-sm hover:scale-110 transition-transform duration-200 cursor-default">
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/10">
                    <span class="text-xl">⚡</span>
                  </div>
                  <div>
                    <div class="text-sm font-semibold text-foreground">Real-time</div>
                    <div class="text-xs text-muted-foreground">Live Updates</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Decorative blur effects -->
          <div
            class="absolute top-0 -translate-y-1/2 left-1/4 w-72 h-72 bg-primary/20 rounded-full blur-3xl -z-10"
            aria-hidden="true" />
          <div
            class="absolute bottom-0 translate-y-1/2 right-1/4 w-72 h-72 bg-secondary/20 rounded-full blur-3xl -z-10"
            aria-hidden="true" />
        </div>

        <!-- Trusted By Section -->
        <div class="max-w-5xl mx-auto mt-32">
          <div class="text-center mb-12">
            <p class="text-sm font-medium text-muted-foreground">
              Trusted by traders on
            </p>
          </div>

          <div class="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
            <!-- Binance -->
            <div class="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity duration-200">
              <img
                src="https://s2.coinmarketcap.com/static/img/exchanges/64x64/270.png"
                alt="Binance"
                class="w-12 h-12 object-contain grayscale hover:grayscale-0 transition-all duration-200"
                loading="lazy" />
              <span class="text-sm font-medium text-muted-foreground">Binance</span>
            </div>

            <!-- Coinbase -->
            <div class="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity duration-200">
              <img
                src="https://s2.coinmarketcap.com/static/img/exchanges/64x64/89.png"
                alt="Coinbase"
                class="w-12 h-12 object-contain grayscale hover:grayscale-0 transition-all duration-200"
                loading="lazy" />
              <span class="text-sm font-medium text-muted-foreground">Coinbase</span>
            </div>

            <!-- Firi -->
            <div class="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity duration-200">
              <img
                src="/images/Firi.svg"
                alt="Firi"
                class="w-12 h-12 object-contain grayscale hover:grayscale-0 transition-all duration-200"
                loading="lazy"
                @error="handleImageError($event, 'firi')" />
              <span class="text-sm font-medium text-muted-foreground">Firi</span>
            </div>

            <!-- Bybit -->
            <div class="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity duration-200">
              <img
                src="https://s2.coinmarketcap.com/static/img/exchanges/64x64/521.png"
                alt="Bybit"
                class="w-12 h-12 object-contain grayscale hover:grayscale-0 transition-all duration-200"
                loading="lazy" />
              <span class="text-sm font-medium text-muted-foreground">Bybit</span>
            </div>

            <!-- Huobi -->
            <div class="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity duration-200">
              <img
                src="https://s2.coinmarketcap.com/static/img/exchanges/64x64/102.png"
                alt="Huobi"
                class="w-12 h-12 object-contain grayscale hover:grayscale-0 transition-all duration-200"
                loading="lazy" />
              <span class="text-sm font-medium text-muted-foreground">Huobi</span>
            </div>

            <!-- Kaupang -->
            <div class="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity duration-200">
              <img
                src="/images/Arcane.svg"
                alt="Kaupang"
                class="w-12 h-12 object-contain grayscale hover:grayscale-0 transition-all duration-200"
                loading="lazy"
                @error="handleImageError($event, 'kaupang')" />
              <span class="text-sm font-medium text-muted-foreground">Kaupang</span>
            </div>
          </div>

          <div class="mt-12 text-center">
            <p class="text-sm text-muted-foreground">
              Seamlessly track trades from all your exchanges in one place
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes pulse-subtle {
  0%, 100% {
    opacity: 0.1;
  }
  50% {
    opacity: 0.15;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-pulse {
  animation: pulse-subtle 8s ease-in-out infinite;
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>
